import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUp,
  Building2,
  ChevronRight,
  Cloud,
  ExternalLink,
  FileImage,
  Film,
  Globe2,
  Home,
  ImagePlus,
  Download,
  Link2,
  LoaderCircle,
  Lock,
  LogOut,
  Menu,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  ShoppingBag,
  Store,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  getMediaProducts,
  getCampaignCdnStatus,
  getPromotion,
  getPromotions,
  getStatus,
  lockEditor,
  readSession,
  publishCampaignDm,
  saveMediaProduct,
  savePromotion,
  unlockEditor,
  writeSession,
} from './api'

const NAV_ITEMS = [
  { key: 'home', label: '管理首頁', icon: Home },
  { key: 'promotions', label: '促銷活動', icon: Sparkles },
  { key: 'media', label: '影音素材', icon: Film },
  { key: 'settings', label: '系統設定', icon: Settings },
]

const CHANNELS = [
  { key: 'show', label: '展售中心', icon: Store },
  { key: 'mart', label: '便利店', icon: ShoppingBag },
  { key: 'eshop', label: '台酒購物網', icon: Globe2 },
  { key: 'office', label: '營業所', icon: Building2 },
]

const COLOR_PRESETS = [
  { label: '飲品系列', value: '#FFF6D5' },
  { label: '美容保養', value: '#FBE1E7' },
  { label: '保健用品', value: '#E3F2FD' },
  { label: '清潔系列', value: '#D8F3DC' },
  { label: '綜合／其他', value: '#FFF3E0' },
]

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

function getPromotionStatus(item) {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const start = item.startDate ? new Date(item.startDate) : null
  const end = item.endDate ? new Date(item.endDate) : null
  if (start && start > now) return { key: 'upcoming', label: '即將開始' }
  if (end) {
    end.setHours(23, 59, 59, 999)
    if (end < now) return { key: 'ended', label: '已結束' }
  }
  return { key: 'active', label: '進行中' }
}

function normalizeMediaLink(link = {}) {
  return {
    type: String(link.type || 'URL').toUpperCase(),
    title: String(link.title || link.label || '相關連結'),
    url: String(link.url || ''),
  }
}

function normalizeChannels(channels = {}) {
  return {
    show: Boolean(channels.show),
    mart: Boolean(channels.mart),
    eshop: Boolean(channels.eshop),
    office: Boolean(channels.office),
  }
}

function normalizeComparableLinks(links = []) {
  return (Array.isArray(links) ? links : [])
    .map(normalizeMediaLink)
    .map((link) => ({
      type: link.type.trim().toUpperCase() || 'URL',
      title: link.title.trim() || '連結',
      url: link.url.trim(),
    }))
    .filter((link) => link.url)
}

function ensurePromotionSaveConfirmed(result, expected) {
  const saved = result?.item
  const expectedChannels = normalizeChannels(expected.channels)
  const actualChannels = normalizeChannels(saved?.channels)

  const confirmed = Boolean(
    result?.ok &&
    saved &&
    String(saved.id) === String(expected.id) &&
    saved.version &&
    String(saved.color || '').toUpperCase() === String(expected.color || '').toUpperCase() &&
    Object.keys(expectedChannels).every((key) => actualChannels[key] === expectedChannels[key]) &&
    (!expected.imageChanged || String(saved.imageUrl || '').trim()) &&
    (typeof expected.isCampaignDm !== 'boolean' || (saved.campaignDmSupported && saved.isCampaignDm === expected.isCampaignDm)) &&
    (!expected.pdfSha256 || (saved.attachment?.pdfSha256 === expected.pdfSha256 && saved.attachment?.pageCount === expected.pdfPageCount))
  )

  if (!confirmed) {
    const error = new Error('儲存結果尚未獲得 Sheet 確認，請先重整確認資料，勿立即重複儲存。')
    error.code = 'SAVE_NOT_CONFIRMED'
    throw error
  }
  return saved
}

function isAmbiguousWriteError(error) {
  return ['WRITE_RESULT_UNCONFIRMED', 'REQUEST_TIMEOUT', 'NETWORK_ERROR'].includes(error?.code)
}

async function confirmPromotionAfterAmbiguousWrite(id, expected, originalError) {
  if (!isAmbiguousWriteError(originalError)) throw originalError

  try {
    const confirmation = await getPromotion(id)
    return ensurePromotionSaveConfirmed(confirmation, expected)
  } catch {
    throw originalError
  }
}

function ensureMediaSaveConfirmed(result, expected) {
  const saved = result?.item
  const expectedLinks = normalizeComparableLinks(expected.moreLinks)
  const actualLinks = normalizeComparableLinks(saved?.moreLinks)

  const confirmed = Boolean(
    result?.ok &&
    saved &&
    String(saved.code) === String(expected.code) &&
    saved.version &&
    String(saved.videoUrl || '').trim() === expected.videoUrl &&
    JSON.stringify(actualLinks) === JSON.stringify(expectedLinks)
  )

  if (!confirmed) {
    const error = new Error('儲存結果尚未獲得 Sheet 確認，請先重整確認資料，勿立即重複儲存。')
    error.code = 'SAVE_NOT_CONFIRMED'
    throw error
  }
  return saved
}

function detectLink(url) {
  const value = String(url || '').trim()
  const lower = value.toLowerCase()
  if (lower.includes('youtu.be') || lower.includes('youtube.com')) return { type: 'YT', title: 'YouTube 影音' }
  if (lower.includes('instagram.com')) return { type: 'IG', title: 'Instagram Reels' }
  if (lower.includes('facebook.com') || lower.includes('fb.watch')) return { type: 'FB', title: 'Facebook 影音' }
  if (lower.includes('threads.net') || lower.includes('threads.com')) return { type: 'THREADS', title: 'Threads 貼文' }
  if (lower.includes('drive.google.com') || lower.includes('1drv.ms') || lower.includes('sharepoint.com') || lower.includes('dropbox.com')) return { type: 'CLOUD', title: '雲端素材' }
  return { type: 'URL', title: '相關連結' }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function requestLayerClose(onClose) {
  if (window.history.state?.__ttlAdminModal) {
    window.history.back()
  } else {
    onClose()
  }
}

function Toast({ toast, onClose }) {
  if (!toast) return null
  return (
    <div className={`toast ${toast.kind || 'info'}`}>
      <span>{toast.message}</span>
      <button onClick={onClose} aria-label="關閉"><X size={16} /></button>
    </div>
  )
}

function Modal({ title, subtitle, onClose, children, footer, wide = false }) {
  useEffect(() => {
    const marker = `modal-${Date.now()}-${Math.random().toString(36).slice(2)}`
    window.history.pushState({ ...(window.history.state || {}), __ttlAdminModal: marker }, '', window.location.href)
    const handlePopState = () => onClose()
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const requestClose = () => requestLayerClose(onClose)

  return (
    <div className="modal-backdrop" onMouseDown={requestClose}>
      <section className={`modal-card ${wide ? 'wide' : ''}`} onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <button className="modal-back-button" onClick={requestClose} aria-label="返回"><ArrowLeft size={21} /></button>
          <div className="modal-title-block">
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button className="icon-button modal-close-button" onClick={requestClose} aria-label="關閉"><X size={20} /></button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </section>
    </div>
  )
}

function EmptyState({ title, message, icon: Icon = Cloud }) {
  return (
    <div className="empty-state">
      <Icon size={34} />
      <h3>{title}</h3>
      <p>{message}</p>
    </div>
  )
}

function LoadingBlock({ label = '資料載入中…' }) {
  return <div className="loading-block"><LoaderCircle className="spin" size={22} />{label}</div>
}

function LoginModal({ onClose, onSuccess, setToast }) {
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    if (!password.trim()) return
    setBusy(true)
    try {
      const result = await unlockEditor(password.trim())
      if (!result?.ok || !result?.token) throw new Error(result?.message || '解鎖失敗')
      const session = { token: result.token, expiresAt: result.expiresAt || null }
      writeSession(session)
      onSuccess(session)
      setToast({ kind: 'success', message: '編輯功能已解鎖' })
      requestLayerClose(onClose)
    } catch (error) {
      setToast({ kind: 'error', message: error.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="解鎖編輯功能" subtitle="瀏覽不需密碼；只有儲存操作需要短效授權。" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>編輯密碼</span>
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus autoComplete="current-password" />
        </label>
        <button className="primary-button full" disabled={busy || !password.trim()}>
          {busy ? <LoaderCircle className="spin" size={18} /> : <Lock size={18} />}
          解鎖
        </button>
      </form>
    </Modal>
  )
}

function Dashboard({ status, promotionCount, mediaCount, onNavigate }) {
  return (
    <div className="page-content">
      <section className="hero-card">
        <div>
          <span className="eyebrow">TTL BIO-TECH ADMIN</span>
          <h1>銷售支援管理中心</h1>
          <p>統一管理促銷檔期、活動圖片、通路設定、商品影音及更多素材。</p>
        </div>
        <div className={`connection-panel ${status?.configured ? 'ok' : 'pending'}`}>
          {status?.configured ? <ShieldCheck size={26} /> : <AlertTriangle size={26} />}
          <div>
            <strong>{status?.configured ? 'API 已連線' : 'API 待設定'}</strong>
            <span>{status?.message || '前端基準已就緒，等待 GAS API 設定。'}</span>
          </div>
        </div>
      </section>

      <section className="metric-grid">
        <article className="metric-card">
          <Sparkles size={22} />
          <span>促銷活動</span>
          <strong>{promotionCount ?? '—'}</strong>
        </article>
        <article className="metric-card">
          <Film size={22} />
          <span>影音商品</span>
          <strong>{mediaCount ?? '—'}</strong>
        </article>
        <article className="metric-card">
          <Cloud size={22} />
          <span>最後同步</span>
          <strong className="small-value">{status?.generatedAt ? formatDate(status.generatedAt) : '尚未連線'}</strong>
        </article>
      </section>

      <section className="module-grid">
        <button className="module-card" onClick={() => onNavigate('promotions')}>
          <div className="module-icon promo"><Sparkles size={28} /></div>
          <div>
            <h2>促銷活動管理</h2>
            <p>查看檔期、調整活動圖片與卡片色系、設定適用通路。</p>
          </div>
          <ChevronRight size={22} />
        </button>
        <button className="module-card" onClick={() => onNavigate('media')}>
          <div className="module-icon media"><Film size={28} /></div>
          <div>
            <h2>影音素材管理</h2>
            <p>維護主要影音與更多素材，手機可直接貼上、排序與預覽。</p>
          </div>
          <ChevronRight size={22} />
        </button>
      </section>
    </div>
  )
}

export function PromotionEditor({ item, canEdit, onClose, onSaved, setToast, saveAction = savePromotion, confirmAction = confirmPromotionAfterAmbiguousWrite }) {
  const [color, setColor] = useState(item.color || '#FFFFFF')
  const [channels, setChannels] = useState({ show: false, mart: false, eshop: false, office: false, ...(item.channels || {}) })
  const [imagePreview, setImagePreview] = useState(item.originalImageUrl || item.imageUrl || item.image || '')
  const [imageOrientation, setImageOrientation] = useState('landscape')
  const [imageFile, setImageFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [isCampaignDm, setIsCampaignDm] = useState(Boolean(item.isCampaignDm))
  const [pdfData, setPdfData] = useState(null)
  const [preparingPdf, setPreparingPdf] = useState(false)
  const selectionRef = useRef(0)
  const dmSupported = Boolean(item.campaignDmSupported)
  const previewImage = isCampaignDm ? pdfData?.coverBase64 || item.attachment?.coverUrl || imagePreview : imagePreview
  useEffect(() => () => { selectionRef.current++ }, [])
  useEffect(() => () => { if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview) }, [imagePreview])

  const handleFile = async (file) => {
    if (!file) return
    const selection = ++selectionRef.current
    const pdfFile = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
    if (pdfFile) {
      if (!dmSupported) { setToast({ kind: 'error', message: '整檔 DM 上傳服務尚未更新，請先完成 GAS 更新。' }); return }
      setPreparingPdf(true)
      try {
        const { prepareCampaignPdf } = await import('./pdfTools.js')
        const prepared = await prepareCampaignPdf(file)
        if (selection !== selectionRef.current) return
        setPdfData(prepared); setIsCampaignDm(true); setImageFile(null)
      } catch (error) { if (selection === selectionRef.current) setToast({ kind: 'error', message: error.message || 'PDF 無法辨識，請重新選取。' }) }
      finally { if (selection === selectionRef.current) setPreparingPdf(false) }
    } else {
      setPreparingPdf(false)
      if (!file.type.startsWith('image/')) { setToast({ kind: 'error', message: '請選取圖片或 PDF。' }); return }
      if (file.size > 5 * 1024 * 1024) { setToast({ kind: 'error', message: '圖片超過 5 MB，請先縮小後再上傳。' }); return }
      setImageFile(file); setImagePreview(URL.createObjectURL(file)); setIsCampaignDm(false); setPdfData(null)
    }
  }

  const save = async () => {
    if (!canEdit || preparingPdf || saving) return
    if (isCampaignDm && !pdfData && !item.attachment?.pdfUrl) { setToast({ kind: 'error', message: '請先上傳整檔 DM 的 PDF。' }); return }
    setSaving(true)
    try {
      const imageData = imageFile ? { base64: await fileToDataUrl(imageFile), name: imageFile.name } : null
      const normalizedChannels = normalizeChannels(channels)
      const expected = {
        id: item.id,
        color,
        channels: normalizedChannels,
        imageChanged: Boolean(imageData),
        isCampaignDm: dmSupported ? isCampaignDm : undefined,
        pdfSha256: isCampaignDm ? pdfData?.sha256 || '' : '',
        pdfPageCount: pdfData?.pageCount || 0,
      }

      let saved
      let recoveredFromDelayedResponse = false
      try {
        const result = await saveAction(item.id, {
          color,
          channels: normalizedChannels,
          imageData,
          ifVersion: item.version || null,
          ...(dmSupported ? { campaignDm: { isCampaignDm, ...(isCampaignDm && pdfData ? { pdfData } : {}) } } : {}),
        })
        saved = ensurePromotionSaveConfirmed(result, expected)
      } catch (error) {
        saved = await confirmAction(item.id, expected, error)
        recoveredFromDelayedResponse = true
      }

      onSaved(saved)
      setToast({
        kind: 'success',
        message: recoveredFromDelayedResponse
          ? '促銷活動已儲存；原回應延遲，已重新確認 Sheet 資料'
          : saved.isCampaignDm && saved.cdnState === 'pending'
            ? '促銷活動已儲存，DM 正在自動發布至圖床'
            : '促銷活動已儲存並確認同步至 Sheet',
      })
      requestLayerClose(onClose)
    } catch (error) {
      setToast({ kind: 'error', message: error.status === 409 ? '資料已被其他入口更新，請重新載入後再編輯。' : error.message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={item.title}
      subtitle={canEdit ? '編輯模式' : '瀏覽模式'}
      onClose={onClose}
      footer={(
        <>
          <button className="secondary-button" onClick={() => requestLayerClose(onClose)}>關閉</button>
          {canEdit ? <button className="primary-button" onClick={save} disabled={saving || preparingPdf}>{saving || preparingPdf ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}{preparingPdf ? '正在產生封面' : saving ? '儲存中' : '儲存設定'}</button> : null}
        </>
      )}
    >
      <div className="stack">
        <div className="dm-option">
          <label><input type="checkbox" checked={isCampaignDm} disabled={!canEdit || !dmSupported || preparingPdf || saving} onChange={event => setIsCampaignDm(event.target.checked)} /><span><strong>整檔DM</strong><small>首頁優先顯示，點開閱讀完整 PDF</small></span></label>
          {!dmSupported ? <p>整檔 DM 上傳服務待更新；一般活動可照常編輯。</p> : null}
        </div>
        <div className="field">
          <span>{isCampaignDm ? 'DM 封面（自動擷取 PDF 第一頁）' : '活動圖片／PDF DM'}</span>
          <div className="image-editor">
            <div className={`image-preview ${imageOrientation}`}>
              {previewImage ? (
                <img
                  src={previewImage}
                  alt={item.title}
                  onLoad={(event) => setImageOrientation(event.currentTarget.naturalHeight > event.currentTarget.naturalWidth ? 'portrait' : 'landscape')}
                />
              ) : <FileImage size={38} />}
            </div>
            <label className={`file-button ${!canEdit || saving ? 'disabled' : ''}`}>
              {preparingPdf ? <LoaderCircle className="spin" size={18} /> : <ImagePlus size={18} />}{preparingPdf ? '正在辨識 PDF…' : dmSupported ? '選擇圖片或 PDF' : '選擇圖片'}
              <input aria-label="上傳活動圖片或 PDF" type="file" accept={dmSupported ? 'image/*,application/pdf,.pdf' : 'image/*'} disabled={!canEdit || saving} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; handleFile(file) }} />
            </label>
          </div>
          {isCampaignDm && (pdfData || item.attachment?.pdfUrl) ? <p className="dm-file-summary"><FileImage size={16} /><span>{pdfData?.name || item.attachment?.pdfName} · {pdfData?.pageCount || item.attachment?.pageCount} 頁</span></p> : null}
          {dmSupported ? <small className="dm-upload-hint">PDF 上限 10 MB，選取後自動辨識頁數與封面。</small> : null}
        </div>

        <div className="field">
          <span>品類</span>
          <div className="preset-grid">
            {COLOR_PRESETS.map((preset) => {
              const selected = String(color).toUpperCase() === preset.value.toUpperCase()
              return (
                <button
                  key={preset.value}
                  type="button"
                  disabled={!canEdit}
                  aria-pressed={selected}
                  className={selected ? 'active' : ''}
                  onClick={() => setColor(preset.value)}
                  style={{ '--preset': preset.value }}
                >
                  <span />{preset.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="field">
          <span>適用通路</span>
          <div className="check-grid">
            {CHANNELS.map((channel) => {
              const Icon = channel.icon
              return (
                <label key={channel.key} className={channels[channel.key] ? 'active' : ''}>
                  <input type="checkbox" disabled={!canEdit} checked={Boolean(channels[channel.key])} onChange={(event) => setChannels((current) => ({ ...current, [channel.key]: event.target.checked }))} />
                  <Icon size={19} />
                  <span>{channel.label}</span>
                </label>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}

const PROMOTION_FILTERS = [
  { key: 'upcoming', label: '即將開始' },
  { key: 'active', label: '進行中' },
  { key: 'ended', label: '已結束' },
]

function PromotionsPage({ canEdit, onRequireUnlock, setToast, onCount }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('upcoming')
  const [activeItem, setActiveItem] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [cdnConfigured, setCdnConfigured] = useState(false)
  const [publicationTick, setPublicationTick] = useState(0)
  const publicationAttempts = useRef(new Set()), publishing = useRef(false), mounted = useRef(true), editable = useRef(canEdit)
  editable.current = canEdit
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getPromotions()
      const next = Array.isArray(result.items) ? result.items : []
      setItems(next)
      setCdnConfigured(result.campaignCdn?.configured === true)
      publicationAttempts.current.clear()
      setLoadError('')
      onCount(next.length)
    } catch (error) {
      setLoadError(error.message)
      setToast({ kind: 'error', message: `促銷資料同步失敗：${error.message}` })
    } finally {
      setLoading(false)
    }
  }, [onCount, setToast])

  useEffect(() => { load() }, [load])

  // Existing Drive DMs migrate automatically on the next normal editing session.
  // Each new saved PDF is picked up by the same queue; there is no second upload.
  useEffect(() => {
    if (!canEdit || loading || !cdnConfigured || publishing.current) return
    const item = items.find(value => value.isCampaignDm && value.attachment?.pdfSha256 && value.cdnState !== 'ready' && !publicationAttempts.current.has(value.id + ':' + value.attachment.pdfSha256))
    if (!item) return
    const pdfSha256 = item.attachment.pdfSha256
    publicationAttempts.current.add(item.id + ':' + pdfSha256)
    publishing.current = true
    const update = (changes, asset) => {
      if (!mounted.current) return
      setItems(current => current.map(value => value.id === item.id && value.attachment?.pdfSha256 === pdfSha256 ? {
        ...value, ...changes,
        ...(asset ? { imageUrl: value.isCampaignDm ? asset.coverUrl : value.imageUrl, attachment: { ...value.attachment, delivery: 'cdn', cdnRevision: asset.cdnRevision, fallbackPdfUrl: value.attachment.fallbackPdfUrl || value.attachment.pdfUrl, fallbackCoverUrl: value.attachment.fallbackCoverUrl || value.attachment.coverUrl, pdfUrl: asset.pdfUrl, coverUrl: asset.coverUrl } } : {}),
      } : value))
    }
    update({ cdnState: 'publishing' })
    ;(async () => {
      try {
        await publishCampaignDm(item.id)
        for (let attempt = 0; attempt < 8 && mounted.current && editable.current; attempt++) {
          const status = await getCampaignCdnStatus(item.id, pdfSha256)
          if (status.ready && status.asset) { update({ cdnState: 'ready' }, status.asset); return }
          await new Promise(resolve => setTimeout(resolve, 4000))
        }
        update({ cdnState: 'deploying' })
      } catch (error) {
        update({ cdnState: 'failed' })
        if (mounted.current) setToast({ kind: 'error', message: 'DM 已儲存；' + error.message })
      } finally {
        publishing.current = false
        if (mounted.current) setPublicationTick(value => value + 1)
      }
    })()
  }, [canEdit, loading, cdnConfigured, items, publicationTick, setToast])

  const statusCounts = items.reduce((counts, item) => {
    counts[getPromotionStatus(item).key] += 1
    return counts
  }, { upcoming: 0, active: 0, ended: 0 })
  const statusLabel = PROMOTION_FILTERS.find((filter) => filter.key === statusFilter).label

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    const list = items.filter((item) => (
      getPromotionStatus(item).key === statusFilter &&
      (!keyword || `${item.title} ${item.id}`.toLowerCase().includes(keyword))
    ))
    return statusFilter === 'ended'
      ? list.sort((a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0))
      : list
  }, [items, query, statusFilter])

  return (
    <div className="page-content">
      <div className="page-heading">
        <div><span className="eyebrow">PROMOTIONS</span><h1>促銷活動管理</h1><p>預設顯示即將開始的活動，可依活動狀態切換與編輯。</p></div>
        <button className="secondary-button" onClick={load}><RefreshCw size={18} />重整</button>
      </div>
      <div className="toolbar-card promotion-toolbar">
        <div className="promotion-filters" role="group" aria-label="活動狀態篩選">
          {PROMOTION_FILTERS.map((filter) => (
            <button key={filter.key} type="button" aria-pressed={statusFilter === filter.key} onClick={() => setStatusFilter(filter.key)}>
              {filter.label}<span>{loading ? '…' : statusCounts[filter.key]}</span>
            </button>
          ))}
        </div>
        <label className="search-box"><Search size={19} /><input value={query} onChange={(event) => setQuery(event.target.value)} aria-label={`搜尋${statusLabel}的活動`} placeholder={`搜尋${statusLabel}的活動名稱或代號…`} /></label>
        <span className={`mode-pill ${canEdit ? 'edit' : ''}`}>{canEdit ? <ShieldCheck size={16} /> : <Lock size={16} />}{canEdit ? '編輯已解鎖' : '瀏覽模式'}</span>
      </div>
      {loading ? <LoadingBlock /> : visible.length ? (
        <div className="promotion-list">
          {visible.map((item) => {
            const status = getPromotionStatus(item)
            const activeChannels = CHANNELS.filter((channel) => item.channels?.[channel.key])
            return (
              <article className="promotion-card" key={item.id} style={{ '--promo-bg': item.color || '#fff' }}>
                <div className="promo-image">{item.imageUrl || item.image ? <img src={item.imageUrl || item.image} alt={item.title} /> : <FileImage size={28} />}</div>
                <div className="promo-main">
                  <div className="promo-title-row"><h2>{item.title}</h2>{item.isCampaignDm ? <span className="dm-badge">整檔 DM</span> : null}<span className={`status ${status.key}`}>{status.label}</span></div>
                  <p>{formatDate(item.startDate)} ～ {formatDate(item.endDate)}</p>
                  <div className="tag-row">{activeChannels.length ? activeChannels.map((channel) => { const Icon = channel.icon; return <span key={channel.key}><Icon size={14} />{channel.label}</span> }) : <span>未設定通路</span>}</div>
                  {item.isCampaignDm ? <div className="dm-delivery-status" role="status">
                    {item.cdnState === 'ready' ? '圖床已就緒' : item.cdnState === 'not-configured' ? '圖床發布尚未啟用' : item.cdnState === 'failed' ? '圖床發布未完成，仍可讀取原始 DM' : item.cdnState === 'deploying' ? '圖床更新中，仍可讀取原始 DM' : item.cdnState === 'publishing' ? '正在發布至圖床…' : '等待自動發布至圖床'}
                    {canEdit && ['failed', 'deploying'].includes(item.cdnState) ? <button type="button" onClick={() => { publicationAttempts.current.delete(item.id + ':' + item.attachment.pdfSha256); setItems(current => current.map(value => value.id === item.id ? { ...value, cdnState: 'pending' } : value)) }}>重試發布</button> : null}
                  </div> : null}
                </div>
                <button className="card-action" onClick={() => setActiveItem(item)}>{canEdit ? '編輯' : '查看'}<ChevronRight size={18} /></button>
              </article>
            )
          })}
        </div>
      ) : loadError ? (
        <EmptyState title="促銷資料暫時無法同步" message={loadError} icon={AlertTriangle} />
      ) : <EmptyState title={query.trim() ? '找不到符合條件的活動' : `目前沒有${statusLabel}的活動`} message={query.trim() ? `「${statusLabel}」中沒有符合搜尋的活動，請調整關鍵字或切換分類。` : '可切換其他活動狀態查看，或按「重整」取得最新資料。'} icon={Sparkles} />}
      {!canEdit ? <button className="floating-unlock" onClick={onRequireUnlock}><Lock size={18} />解鎖編輯</button> : null}
      {activeItem ? <PromotionEditor item={activeItem} canEdit={canEdit} onClose={() => setActiveItem(null)} setToast={setToast} onSaved={(saved) => setItems((current) => current.map((item) => item.id === saved.id ? saved : item))} /> : null}
    </div>
  )
}

function MediaEditor({ item, canEdit, onClose, onSaved, setToast }) {
  const [videoUrl, setVideoUrl] = useState(item.videoUrl || item.video_url || '')
  const [links, setLinks] = useState((item.moreLinks || []).map(normalizeMediaLink))
  const [saving, setSaving] = useState(false)

  const getUrlState = (value) => {
    const normalized = String(value || '').trim()
    if (!normalized) return 'empty'
    return /^https?:\/\//i.test(normalized) ? 'valid' : 'invalid'
  }

  const platformLabel = (type) => ({
    YT: 'YouTube',
    IG: 'Instagram',
    FB: 'Facebook',
    THREADS: 'Threads',
    CLOUD: '雲端素材',
    URL: '其他連結',
  }[String(type || 'URL').toUpperCase()] || '其他連結')

  const updateLink = (index, patch) => setLinks((current) => current.map((link, linkIndex) => linkIndex === index ? { ...link, ...patch } : link))
  const addLink = () => setLinks((current) => [...current, { type: 'URL', title: '相關連結', url: '' }])
  const removeLink = (index) => setLinks((current) => current.filter((_, linkIndex) => linkIndex !== index))

  const updateLinkUrl = (index, value) => {
    setLinks((current) => current.map((link, linkIndex) => {
      if (linkIndex !== index) return link
      const detected = detectLink(value)
      const genericTitles = ['相關連結', '連結', 'YouTube 影音', 'Instagram Reels', 'Facebook 影音', 'Threads 貼文', '雲端素材']
      const shouldAutoTitle = !String(link.title || '').trim() || genericTitles.includes(String(link.title || '').trim())
      return {
        ...link,
        url: value,
        type: getUrlState(value) === 'valid' ? detected.type : link.type,
        title: getUrlState(value) === 'valid' && shouldAutoTitle ? detected.title : link.title,
      }
    }))
  }

  const save = async () => {
    if (!canEdit) return
    const invalid = [videoUrl, ...links.map((link) => link.url)].filter(Boolean).find((url) => !/^https?:\/\//i.test(url))
    if (invalid) {
      setToast({ kind: 'error', message: '網址需以 http:// 或 https:// 開頭' })
      return
    }
    setSaving(true)
    try {
      const normalizedLinks = normalizeComparableLinks(
        links
          .filter((link) => link.url.trim())
          .map((link) => ({ ...link, title: link.title.trim() || detectLink(link.url).title })),
      )
      const expected = {
        code: item.code,
        videoUrl: videoUrl.trim(),
        moreLinks: normalizedLinks,
      }
      const result = await saveMediaProduct(item.code, {
        videoUrl: expected.videoUrl,
        moreLinks: expected.moreLinks,
        ifVersion: item.version || null,
      })
      const saved = ensureMediaSaveConfirmed(result, expected)
      onSaved(saved)
      setToast({ kind: 'success', message: '影音素材已儲存並確認同步至 Sheet' })
      requestLayerClose(onClose)
    } catch (error) {
      setToast({ kind: 'error', message: error.status === 409 ? '資料已被其他入口更新，請重新載入後再編輯。' : error.message })
    } finally {
      setSaving(false)
    }
  }

  const videoState = getUrlState(videoUrl)
  const videoPlatform = videoState === 'valid' ? detectLink(videoUrl).title : ''

  return (
    <Modal
      wide
      title={item.name}
      subtitle={`${canEdit ? '編輯模式' : '瀏覽模式'}｜產品代號 ${item.code}`}
      onClose={onClose}
      footer={(
        <>
          <button className="secondary-button" onClick={() => requestLayerClose(onClose)}>關閉</button>
          {canEdit ? <button className="primary-button" onClick={save} disabled={saving}>{saving ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}儲存變更</button> : null}
        </>
      )}
    >
      <div className="stack media-editor-stack">
        <section className={`media-editor-section primary-media-section url-state-${videoState}`}>
          <div className="media-editor-section-head primary">
            <div className="media-section-icon"><Film size={21} /></div>
            <div>
              <strong>主要影音</strong>
              <span>放置最主要的商品介紹影片或影音頁面</span>
            </div>
          </div>
          <div className="media-editor-section-body">
            <label className="media-url-label" htmlFor="primary-media-url">
              <span>完整影音網址</span>
              <small>支援 YouTube、Facebook、Instagram 與一般 HTTPS 網址。</small>
            </label>
            <div className="input-action-row media-url-row">
              <input
                id="primary-media-url"
                className="media-url-input"
                value={videoUrl}
                readOnly={!canEdit}
                inputMode="url"
                placeholder="https://…"
                onChange={(event) => setVideoUrl(event.target.value)}
              />
              {videoUrl ? <a className="icon-button" href={videoUrl} target="_blank" rel="noreferrer" aria-label="開啟主要影音"><ExternalLink size={18} /></a> : null}
            </div>
            <div className={`url-feedback ${videoState}`}>
              {videoState === 'valid' ? `已辨識：${videoPlatform}` : videoState === 'invalid' ? '網址格式不完整，請確認以 http:// 或 https:// 開頭。' : '尚未設定主要影音，可保留空白。'}
            </div>
          </div>
        </section>

        <section className="media-editor-section more-media-section">
          <div className="media-editor-section-head secondary">
            <div className="media-section-icon"><Link2 size={21} /></div>
            <div>
              <strong>更多素材</strong>
              <span>可加入教學影片、社群貼文、雲端檔案或活動頁面</span>
            </div>
            {canEdit ? <button className="small-button emphasized" onClick={addLink}><Plus size={16} />新增素材</button> : null}
          </div>
          <div className="media-editor-section-body">
            <div className="link-editor-list">
              {links.length ? links.map((link, index) => {
                const state = getUrlState(link.url)
                const platform = String(link.type || 'URL').toLowerCase()
                return (
                  <article className={`link-editor-card guided platform-${platform} url-state-${state}`} key={index}>
                    <div className="link-number">{index + 1}</div>
                    <div className="link-card-content">
                      <div className="link-card-heading">
                        <strong>{platformLabel(link.type)}</strong>
                        <span className={`url-mini-status ${state}`}>{state === 'valid' ? '網址有效' : state === 'invalid' ? '格式錯誤' : '尚未填寫'}</span>
                      </div>
                      <div className="link-fields link-fields-guided">
                        <label className="link-field-block">
                          <span>平台類型</span>
                          <select disabled={!canEdit} value={link.type} onChange={(event) => updateLink(index, { type: event.target.value })}>
                            <option value="YT">YouTube</option><option value="IG">Instagram</option><option value="FB">Facebook</option><option value="THREADS">Threads</option><option value="CLOUD">雲端</option><option value="URL">其他連結</option>
                          </select>
                        </label>
                        <label className="link-field-block">
                          <span>素材標題</span>
                          <input readOnly={!canEdit} value={link.title} placeholder="例如：商品使用方式" onChange={(event) => updateLink(index, { title: event.target.value })} />
                        </label>
                        <label className="link-field-block url-field">
                          <span>完整網址</span>
                          <input readOnly={!canEdit} value={link.url} inputMode="url" placeholder="https://…" onChange={(event) => updateLinkUrl(index, event.target.value)} />
                          <small className={`inline-url-help ${state}`}>{state === 'valid' ? `已自動辨識為 ${platformLabel(link.type)}` : state === 'invalid' ? '請貼上完整網址，並確認包含 http:// 或 https://。' : '貼上網址後會自動辨識平台與建議標題。'}</small>
                        </label>
                      </div>
                    </div>
                    <div className="link-actions">
                      {link.url && state === 'valid' ? <a className="icon-button" href={link.url} target="_blank" rel="noreferrer" aria-label="開啟素材"><ExternalLink size={17} /></a> : null}
                      {canEdit ? <button className="icon-button danger" onClick={() => removeLink(index)} aria-label="刪除素材"><Trash2 size={17} /></button> : null}
                    </div>
                  </article>
                )
              }) : (
                <div className="media-empty-guide">
                  <Link2 size={30} />
                  <h3>尚未新增其他素材</h3>
                  <p><span>1</span>按下「新增素材」　<span>2</span>貼上完整網址　<span>3</span>系統自動辨識平台</p>
                  {canEdit ? <button className="small-button emphasized" onClick={addLink}><Plus size={16} />新增第一筆素材</button> : null}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </Modal>
  )
}

function MediaPage({ canEdit, onRequireUnlock, setToast, onCount }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [activeItem, setActiveItem] = useState(null)
  const [loadError, setLoadError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getMediaProducts()
      const next = Array.isArray(result.items) ? result.items.map((item) => ({ ...item, moreLinks: Array.isArray(item.moreLinks) ? item.moreLinks : [] })) : []
      setItems(next)
      setLoadError('')
      onCount(next.length)
    } catch (error) {
      setLoadError(error.message)
      setToast({ kind: 'error', message: `影音資料同步失敗：${error.message}` })
    } finally {
      setLoading(false)
    }
  }, [onCount, setToast])

  useEffect(() => { load() }, [load])

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    return keyword ? items.filter((item) => `${item.code} ${item.name}`.toLowerCase().includes(keyword)) : items
  }, [items, query])

  return (
    <div className="page-content">
      <div className="page-heading">
        <div><span className="eyebrow">MEDIA LIBRARY</span><h1>影音素材管理</h1><p>維護主要影音與更多素材，網址會自動辨識平台類型。</p></div>
        <button className="secondary-button" onClick={load}><RefreshCw size={18} />重整</button>
      </div>
      <div className="toolbar-card">
        <label className="search-box"><Search size={19} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜尋產品代號或名稱…" /></label>
        <span className={`mode-pill ${canEdit ? 'edit' : ''}`}>{canEdit ? <ShieldCheck size={16} /> : <Lock size={16} />}{canEdit ? '編輯已解鎖' : '瀏覽模式'}</span>
      </div>
      {loading ? <LoadingBlock /> : visible.length ? (
        <div className="media-list">
          {visible.map((item) => {
            const hasVideo = Boolean(item.videoUrl || item.video_url)
            const moreCount = item.moreLinks?.length || 0
            return (
              <article className="media-card" key={item.code}>
                <div className="media-code">{item.code}</div>
                <div className="media-main"><h2>{item.name}</h2><div className="tag-row"><span className={hasVideo ? 'positive' : ''}>{hasVideo ? '已有主要影音' : '無主要影音'}</span><span className={moreCount ? 'positive' : ''}>更多素材 {moreCount} 筆</span></div></div>
                <button className="card-action" onClick={() => setActiveItem(item)}>{canEdit ? '編輯' : '查看'}<ChevronRight size={18} /></button>
              </article>
            )
          })}
        </div>
      ) : loadError ? (
        <EmptyState title="影音資料暫時無法同步" message={loadError} icon={AlertTriangle} />
      ) : <EmptyState title="目前沒有影音資料" message="目前 Sheet 中沒有可顯示的商品影音資料。" icon={Film} />}
      {!canEdit ? <button className="floating-unlock" onClick={onRequireUnlock}><Lock size={18} />解鎖編輯</button> : null}
      {activeItem ? <MediaEditor item={activeItem} canEdit={canEdit} onClose={() => setActiveItem(null)} setToast={setToast} onSaved={(saved) => setItems((current) => current.map((item) => item.code === saved.code ? saved : item))} /> : null}
    </div>
  )
}

function SettingsPage({ status }) {
  return (
    <div className="page-content">
      <div className="page-heading"><div><span className="eyebrow">SYSTEM</span><h1>系統設定</h1><p>檢視 Cloudflare Pages、API Gateway 與 GAS 串接狀態。</p></div></div>
      <section className="settings-grid">
        <article className="settings-card"><Cloud size={24} /><div><h2>Cloudflare Pages</h2><p>正式站預定為 <code>ttlbio-manage.pages.dev</code></p></div><span className="status active">前端已就緒</span></article>
        <article className="settings-card"><ShieldCheck size={24} /><div><h2>API Gateway</h2><p>同源路徑 <code>/api/*</code>，GAS URL 與 Gateway Key 僅存於 Cloudflare 環境變數。</p></div><span className={`status ${status?.configured ? 'active' : 'upcoming'}`}>{status?.configured ? '已連線' : '待設定'}</span></article>
        <article className="settings-card"><RefreshCw size={24} /><div><h2>舊 GAS 管理頁</h2><p>兩個既有入口保留，與新站並行使用；儲存時透過版本欄避免覆蓋較新的修改。</p></div><span className="status active">保留並行</span></article>
      </section>
      <div className="notice-card"><AlertTriangle size={20} /><div><strong>並行操作注意</strong><p>同一筆資料若同時在舊 GAS 頁與新站開啟，後儲存者可能遇到版本衝突；重新載入後再修改即可，不會靜默覆蓋。</p></div></div>
    </div>
  )
}

export default function App() {
  const [page, setPage] = useState('home')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [status, setStatus] = useState(null)
  const [session, setSession] = useState(() => readSession())
  const [loginOpen, setLoginOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [promotionCount, setPromotionCount] = useState(null)
  const [mediaCount, setMediaCount] = useState(null)
  const [installPrompt, setInstallPrompt] = useState(null)
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    getStatus().then(setStatus).catch((error) => setStatus({ configured: false, message: error.message }))
  }, [])

  useEffect(() => {
    const initialState = window.history.state || {}
    if (!initialState.__ttlAdminPage) {
      window.history.replaceState({ ...initialState, __ttlAdminPage: 'home' }, '', window.location.href)
    }

    const handlePopState = (event) => {
      if (event.state?.__ttlAdminModal) return
      setPage(event.state?.__ttlAdminPage || 'home')
      setSidebarOpen(false)
      window.scrollTo({ top: 0, behavior: 'auto' })
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    const handler = (event) => {
      event.preventDefault()
      setInstallPrompt(event)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 360)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navigate = (nextPage) => {
    if (nextPage !== page) {
      const { __ttlAdminModal, ...currentState } = window.history.state || {}
      window.history.pushState({ ...currentState, __ttlAdminPage: nextPage }, '', window.location.href)
    }
    setPage(nextPage)
    setSidebarOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const logout = async () => {
    await lockEditor().catch(() => null)
    setSession(null)
    setToast({ kind: 'success', message: '已返回瀏覽模式' })
  }

  const install = async () => {
    if (!installPrompt) return
    await installPrompt.prompt()
    setInstallPrompt(null)
  }

  const activeNav = NAV_ITEMS.find((item) => item.key === page) || NAV_ITEMS[0]
  const ActiveIcon = activeNav.icon

  return (
    <div className="app-shell">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="brand-block"><div className="brand-mark">TTL</div><div><strong>Bio-Tech Admin</strong><span>銷售支援管理中心</span></div></div>
        <nav>{NAV_ITEMS.map((item) => { const Icon = item.icon; return <button key={item.key} className={page === item.key ? 'active' : ''} onClick={() => navigate(item.key)}><Icon size={20} />{item.label}</button> })}</nav>
        <div className="sidebar-footer">
          {installPrompt ? <button onClick={install}><Download size={18} />安裝管理 App</button> : null}
          {session ? <button onClick={logout}><LogOut size={18} />返回瀏覽模式</button> : <button onClick={() => setLoginOpen(true)}><Lock size={18} />解鎖編輯</button>}
        </div>
      </aside>
      {sidebarOpen ? <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="關閉選單" /> : null}

      <main className="main-area">
        <header className="topbar">
          <button className="menu-button" onClick={() => setSidebarOpen(true)} aria-label="開啟選單"><Menu size={21} /></button>
          <div className="topbar-title"><ActiveIcon size={19} /><strong>{activeNav.label}</strong></div>
          <div className="topbar-actions"><span className={`mode-pill ${session ? 'edit' : ''}`}>{session ? <ShieldCheck size={15} /> : <Lock size={15} />}{session ? '可編輯' : '瀏覽'}</span></div>
        </header>

        {page === 'home' ? <Dashboard status={status} promotionCount={promotionCount} mediaCount={mediaCount} onNavigate={navigate} /> : null}
        {page === 'promotions' ? <PromotionsPage canEdit={Boolean(session)} onRequireUnlock={() => setLoginOpen(true)} setToast={setToast} onCount={setPromotionCount} /> : null}
        {page === 'media' ? <MediaPage canEdit={Boolean(session)} onRequireUnlock={() => setLoginOpen(true)} setToast={setToast} onCount={setMediaCount} /> : null}
        {(page === 'promotions' || page === 'media') && showScrollTop ? (
          <button
            className={`floating-top ${session ? 'solo' : ''}`}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="回到頁面頂端"
          >
            <ArrowUp size={18} />置頂
          </button>
        ) : null}
        {page === 'settings' ? <SettingsPage status={status} /> : null}
      </main>

      {loginOpen ? <LoginModal onClose={() => setLoginOpen(false)} onSuccess={setSession} setToast={setToast} /> : null}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  )
}
